/**
 * The hand-made walls. Rows alternate like a real brick wall: even rows hold six 2-stud bricks,
 * odd rows a half brick, five 2-stud bricks and a half brick. Each letter is one brick in that
 * order — R/Y/B/G a color, K black (two hits), '.' no brick. Golden bricks are added per game.
 */
import { BRICK_COLORS, BrickKind, COLS, GOLD } from "./world";

export interface Brick {
  id: number;
  col: number;
  row: number;
  len: 1 | 2;
  kind: BrickKind;
  color: string;
  /** Hits left: 1 for plain and gold, 2 for a fresh black brick. */
  hits: number;
}

export interface Layout {
  top: number;
  rows: string[];
}

export const LAYOUTS: Layout[] = [
  // 1: a plain wall with a black top course.
  { top: 2, rows: ["KKKKKK", "RRRRRRR", "YYYYYY", "BBBBBBB", "GGGGGG", "RRRRRRR"] },
  // 2: black bricks spread through it.
  { top: 1, rows: ["Y.YY.Y", "RRKRKRR", "BBBBBB", "GKGGGKG", "YYYYYY", "R.RRR.R", "KBBBBK"] },
  // 3: a black frame.
  { top: 1, rows: ["KYYYYK", "RRRRRRR", "B.BB.B", "GGKGKGG", "YYYYYY", "RRRRRRR", "BBKKBB"] },
  // 4: a tall wall with a black core.
  { top: 1, rows: ["..KK..", ".RRRRR.", "YYKKYY", "BBBBBBB", "GKGGKG", "RRRRRRR", "YYYYYY", "KBBBBBK"] },
];

const EVEN = [2, 2, 2, 2, 2, 2] as const;
const ODD = [1, 2, 2, 2, 2, 2, 1] as const;

/** Bricks of one layout (cycling after the last), with ids from 0. No golden bricks yet. */
export function buildWall(layoutIndex: number): Brick[] {
  return parseWall(LAYOUTS[((layoutIndex % LAYOUTS.length) + LAYOUTS.length) % LAYOUTS.length]);
}

/**
 * Bricks from layout rows. Besides the layout letters, pictures may use 'O' (golden) and
 * 'k' (black, already cracked).
 */
export function parseWall(layout: Layout): Brick[] {
  const bricks: Brick[] = [];
  layout.rows.forEach((row, i) => {
    const lens = i % 2 === 0 ? EVEN : ODD;
    if (row.length !== lens.length) throw new Error(`Layout row "${row}" should have ${lens.length} bricks`);
    let col = 0;
    lens.forEach((len, j) => {
      const ch = row[j];
      if (ch !== ".") {
        const black = ch === "K" || ch === "k";
        const gold = ch === "O";
        bricks.push({
          id: bricks.length,
          col,
          row: layout.top + i,
          len,
          kind: black ? "black" : gold ? "gold" : "plain",
          color: gold ? GOLD.mid : BRICK_COLORS[black ? "K" : ch],
          hits: ch === "K" ? 2 : 1,
        });
      }
      col += len;
    });
    if (col !== COLS) throw new Error("Layout rows must span the field");
  });
  return bricks;
}
